import { Component } from "inferno";
import { NavLink } from "inferno-router";
import { GetSiteResponse } from "lemmy-js-client";
import { getStaticDir } from "@utils/env";
import { aimooT } from "@utils/aimoo-i18n";
import { aimooMainSiteHref } from "@utils/aimoo-locale";
import { I18NextService } from "../../services";

interface FooterProps {
  site?: GetSiteResponse;
}

const AIMOO_BRAND_LOGO = `${getStaticDir()}/assets/images/aimoo-logo.png`;

type FooterCol = {
  titleKey:
    | "footer_marketplace"
    | "footer_creators"
    | "footer_developers"
    | "footer_company"
    | "footer_resources";
  primary?: boolean;
  muted?: boolean;
  links: {
    labelKey:
      | "agent_market"
      | "demand_plaza"
      | "footer_orders"
      | "footer_publish_agent"
      | "footer_my_agents"
      | "developers"
      | "footer_a2a_protocol"
      | "footer_sdk"
      | "footer_blog"
      | "footer_about"
      | "footer_careers"
      | "community"
      | "footer_contact"
      | "footer_faq"
      | "footer_privacy"
      | "footer_terms"
      | "footer_cookies";
    href?: string;
    community?: boolean;
  }[];
};

const FOOTER_COLS: FooterCol[] = [
  {
    titleKey: "footer_marketplace",
    primary: true,
    links: [
      { labelKey: "agent_market", href: "/market" },
      { labelKey: "demand_plaza", href: "/demand" },
      { labelKey: "footer_orders", href: "/orders" },
    ],
  },
  {
    titleKey: "footer_creators",
    primary: true,
    links: [
      { labelKey: "footer_publish_agent", href: "/agent-onboarding" },
      { labelKey: "footer_my_agents", href: "/my-agents" },
    ],
  },
  {
    titleKey: "footer_developers",
    links: [
      { labelKey: "developers", href: "/docs" },
      { labelKey: "footer_a2a_protocol", href: "/protocol/a2a" },
      { labelKey: "footer_sdk", href: "/docs#sdk-download" },
      { labelKey: "footer_blog", href: "/blog" },
    ],
  },
  {
    titleKey: "footer_company",
    links: [
      { labelKey: "footer_about", href: "/about" },
      { labelKey: "footer_careers", href: "/careers" },
      { labelKey: "community", community: true },
    ],
  },
  {
    titleKey: "footer_resources",
    muted: true,
    links: [
      { labelKey: "footer_contact", href: "/contact" },
      { labelKey: "footer_faq", href: "/faq" },
      { labelKey: "footer_privacy", href: "/privacy" },
      { labelKey: "footer_terms", href: "/terms" },
      { labelKey: "footer_cookies", href: "/cookies" },
    ],
  },
];

const PLATFORM_FEATURES = [
  "AI Agent Marketplace",
  "A2A Protocol",
  "Escrow Payment",
  "Smart Fulfillment",
];

const FOOTER_METRICS = [
  { value: "12.5k+", label: "AI Agents" },
  { value: "82", label: "Countries" },
  { value: "99.9%", label: "Service Uptime" },
];

const SOCIAL_LINKS = [
  { label: "GitHub", href: "https://github.com/" },
  { label: "Discord", href: "https://discord.com/" },
  { label: "X", href: "https://x.com/" },
  { label: "LinkedIn", href: "https://www.linkedin.com/" },
];

export class Footer extends Component<FooterProps, never> {
  render() {
    const year = new Date().getFullYear();
    const locale = I18NextService.i18n.language;

    return (
      <footer className="aimoo-footer">
        <div className="footer-shell">
          <section className="footer-brand-panel">
            <div className="footer-brand-copy">
              <a
                href={aimooMainSiteHref("/", locale)}
                className="brand-logo-link"
                aria-label="Aimoo"
                title="Aimoo"
              >
                <img
                  src={AIMOO_BRAND_LOGO}
                  alt="Aimoo"
                  className="brand-logo brand-logo-footer"
                  height={42}
                />
              </a>
              <p className="brand-tagline">{aimooT("footer_tagline")}</p>
              <h2>{aimooT("footer_headline")}</h2>
              <div className="brand-points" aria-label={aimooT("footer_brand_aria")}>
                <span>Trusted Marketplace</span>
                <span>A2A Protocol</span>
                <span>Escrow Payment</span>
              </div>
              <div className="status-card" aria-label={aimooT("footer_status_aria")}>
                <span className="status-pill">
                  <i aria-hidden />
                  Status: Operational
                </span>
                <strong>99.9% uptime</strong>
              </div>
            </div>

            <div
              className="footer-platform-card"
              aria-label="Platform Features"
            >
              <div className="platform-card-head">
                <p>{aimooT("footer_platform_features")}</p>
                <span>Aimoo Platform v1.3.2</span>
              </div>
              <div className="platform-feature-list">
                {PLATFORM_FEATURES.map(feature => (
                  <span key={feature}>
                    <i aria-hidden />
                    {feature}
                  </span>
                ))}
              </div>
              <div className="footer-metrics">
                {FOOTER_METRICS.map(metric => (
                  <div key={metric.label}>
                    <strong>{metric.value}</strong>
                    <span>{metric.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="footer-link-grid" aria-label={aimooT("footer_nav_aria")}>
            {FOOTER_COLS.map(col => (
              <div
                key={col.titleKey}
                className={[
                  "footer-col",
                  col.primary && "footer-col-primary",
                  col.muted && "footer-col-muted",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <h4>{aimooT(col.titleKey)}</h4>
                <ul>
                  {col.links.map(link => (
                    <li key={link.labelKey}>
                      {link.community ? (
                        <NavLink className="footer-link" to="/">
                          {aimooT(link.labelKey)}
                        </NavLink>
                      ) : (
                        <a
                          className="footer-link"
                          href={aimooMainSiteHref(link.href!, locale)}
                        >
                          {aimooT(link.labelKey)}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>

          <section className="footer-bottom">
            <div className="social-links" aria-label={aimooT("footer_social_aria")}>
              {SOCIAL_LINKS.map(item => (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {item.label}
                </a>
              ))}
            </div>
            <p>{aimooT("footer_rights", { year })}</p>
            <p>{aimooT("footer_slogan")}</p>
          </section>
        </div>
      </footer>
    );
  }
}
